import { useEffect, useState } from 'react';
import { Song, SongPreview, VideoProvider } from '~/interfaces';
import SongDao from '~/modules/Songs/SongsService';
import convertTxtToSong from '~/modules/Songs/utils/convertTxtToSong';
import { processSong } from '~/modules/Songs/utils/processSong/processSong';
import { generateFakeUltrastarTxt } from '~/modules/Songs/utils/fakeUltrastar';

let karaokeIndexCache: Map<string, SongPreview> | null = null;

async function getKaraokePreview(songId: string): Promise<SongPreview | null> {
  if (!karaokeIndexCache) {
    try {
      const res = await fetch('/karaoke/index.json');
      if (!res.ok) {
        karaokeIndexCache = new Map();
        return null;
      }
      const data = (await res.json()) as SongPreview[];
      karaokeIndexCache = new Map(data.map((s) => [s.id, s]));
    } catch {
      karaokeIndexCache = new Map();
      return null;
    }
  }

  return karaokeIndexCache.get(songId) ?? null;
}

function isValidLoadedSong(song: Song | null): song is Song {
  if (!song) return false;
  if (!song.video || song.video.trim().length === 0) return false;
  if (Number.isNaN(song.videoGap as unknown as number)) return false;
  return true;
}

function ensureValidBpm(bpm: unknown, fallback = 120) {
  const n = Number(bpm);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.round(n);
}

function inferProvider(preview: Pick<SongPreview, 'provider' | 'uid' | 'url' | 'id'>): VideoProvider {
  if (preview.provider) return preview.provider;

  if (preview.uid?.startsWith('dailymotion:')) return 'dailymotion';
  if (preview.uid?.startsWith('vimeo:')) return 'vimeo';
  if (preview.uid?.startsWith('youtube:')) return 'youtube';

  if (preview.url?.includes('dailymotion.com')) return 'dailymotion';
  if (preview.url?.includes('vimeo.com')) return 'vimeo';
  if (preview.url?.includes('youtube.com') || preview.url?.includes('youtu.be')) return 'youtube';

  if (preview.id?.startsWith('dailymotion-')) return 'dailymotion';
  if (preview.id?.startsWith('vimeo-')) return 'vimeo';

  return 'youtube';
}

export default function useSong(songId: string) {
  const [song, setSong] = useState<Song | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // 1) intento normal
        const loaded = await SongDao.get(songId);

        if (isValidLoadedSong(loaded)) {
          const processed = processSong(loaded);
          if (!cancelled) setSong(processed);
          return;
        }

        // 2) karaoke mode
        const preview = await getKaraokePreview(songId);
        if (!preview) {
          if (!cancelled) setSong(null);
          return;
        }

        const bpm = ensureValidBpm(preview.bpm, 120);

        const fakeTxt = generateFakeUltrastarTxt(
          {
            id: preview.id,
            title: preview.title,
            artist: preview.artist,
            video: preview.video,
            language: preview.language,
            year: preview.year,
          },
          240,
        );

        const parsed = convertTxtToSong(fakeTxt);
        const provider = inferProvider(preview);

        const karaokeSong: Song = {
          ...parsed,

          id: preview.id,
          shortId: preview.shortId ?? parsed.shortId,

          title: preview.title,
          artist: preview.artist,
          video: preview.video,

          provider,
          uid: preview.uid ?? `${provider}:${preview.video}`,
          url: preview.url,

          language: preview.language ?? parsed.language,
          year: preview.year ?? parsed.year,
          author: preview.author ?? parsed.author,
          authorUrl: preview.authorUrl ?? parsed.authorUrl,
          sourceUrl: preview.sourceUrl ?? parsed.sourceUrl,

          bpm,
          gap: (preview.gap ?? parsed.gap ?? 0) as Song['gap'],
          trackOffsetsMs: preview.trackOffsetsMs,
          videoGap: Number(preview.videoGap ?? 0),

          manualVolume: preview.manualVolume ?? parsed.manualVolume,
          volume: preview.volume ?? parsed.volume,
          lastUpdate: preview.lastUpdate ?? parsed.lastUpdate,

          local: false,
        };

        const processed = processSong(karaokeSong);
        if (!cancelled) setSong(processed);
      } catch {
        if (!cancelled) setSong(null);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [songId]);

  return {
    data: song,
  };
}